import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ProductionTable from '../my-production/ProductionTable';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';
import StationNotesModal, { StationProblemType } from './StationNotesModal';

/** Przejściówka weight, e.g. "1,25" — up to 3 decimals, Polish decimal comma. */
const formatKg = (kg: number): string => kg.toLocaleString('pl-PL', { maximumFractionDigits: 3 });

interface WarehouseItem {
  id: number;
  przewinieta: boolean;
  rewound_z1: number;
  rewound_z2: number;
  przewinieta_at: string | null;
  // Uwaga stacji przewijania — niezależna od uwagi linii.
  notes?: string | null;
  notes_by?: string | null;
  notes_at?: string | null;
  przewinieta_by: string | null;
  // Rewinding machines used and the people who rewound the spools, in the order it happened.
  rewinders?: string[];
  rewound_by?: string[];
  // Przejściówka marked by the operators after rewinding, total kg (null = none marked).
  transition_kg?: number | null;
  completed_by: string | null;
  created_at: string;
  production_item_id: number;
  production_assignment_id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  ean: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  uwagi: string | null;
  barwnik_dostarczony?: boolean;
  priorytet?: boolean;
}

// Read-only view of an item's rewound pieces — entering them moved to the WorkSpectrum
// rewinding station, which reports every finished rewinding here.
interface HistoryModalState {
  item: WarehouseItem;
  shift: 1 | 2;
}

export default function LargeWarehouseView() {
  const { isManager, user } = useAuth();
  // Cofnięcie przewinięcia zmienia nadwyżki materiałowe — dostępne tylko dla admina.
  const canRestoreRewinding = user?.role === 'admin';
  const { t, language } = useLanguage();
  const dateLocale = language === 'en' ? 'en-GB' : language === 'ru' ? 'uk-UA' : 'pl-PL';
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Przywrócenie omyłkowo zakończonej pozycji z „Przewinięte" z powrotem do przewijania.
  const [restoreItem, setRestoreItem] = useState<WarehouseItem | null>(null);
  const [restoring, setRestoring] = useState(false);
  // Cofnięcie oczekującej pozycji ze stacji z powrotem do „Mojej produkcji".
  const [removeItem, setRemoveItem] = useState<WarehouseItem | null>(null);
  const [removing, setRemoving] = useState(false);
  // Uwagi stacji (własne, nie z linii) — modal + lista typów problemów.
  const [notesItem, setNotesItem] = useState<WarehouseItem | null>(null);
  const [problemTypes, setProblemTypes] = useState<StationProblemType[]>([]);
  const [historyModal, setHistoryModal] = useState<HistoryModalState | null>(null);
  const [rewindHistory, setRewindHistory] = useState<any[]>([]);
  const [loadingRewindHistory, setLoadingRewindHistory] = useState(false);
  // Line filter (several lines can be selected, empty = all) — narrows both tables.
  const [lineFilter, setLineFilter] = useState<string[]>([]);
  // Week filter (several weeks can be selected, empty = all) — narrows both tables, independent of the line filter.
  const [weekFilter, setWeekFilter] = useState<string[]>([]);
  // Search box — narrows both tables and the per-line summary like the week filter.
  const [search, setSearch] = useState('');
  const [wagaProg, setWagaProg] = useState<string>('20');
  const [savingProg, setSavingProg] = useState(false);
  const [progInput, setProgInput] = useState<string>('20');

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/warehouse/large');
      setItems(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await api.get('/settings');
      const prog = res.data?.waga_prog?.value ?? '20';
      setWagaProg(prog);
      setProgInput(prog);
    } catch {
      // silent
    }
  }, [isManager]);

  useEffect(() => {
    fetchItems();
    fetchSettings();
    api.get('/problem-types').then((res) => setProblemTypes(res.data || [])).catch(() => setProblemTypes([]));
  }, [fetchItems, fetchSettings]);

  // The station is a preview now; the only action on a pending row is the admin's
  // "send back to My production" for an item sent here by mistake.
  const handleRowClick = (item: WarehouseItem) => {
    if (!item.przewinieta && canRestoreRewinding) setRemoveItem(item);
  };

  // Przywraca zakończoną pozycję do przewijania (np. gdy ktoś omyłkowo zamknął pozycję po
  // wpisaniu nadwyżki). Backend kasuje znacznik „przewinięta", przez co pozycja znika
  // z nadwyżek i wraca na niebiesko do oczekujących — ilość poprawia się tu ręcznie.
  const handleRestoreConfirm = async () => {
    if (!restoreItem) return;
    setRestoring(true);
    try {
      await api.post(`/warehouse/large/${restoreItem.id}/restore`);
      await fetchItems();
      setRestoreItem(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || t('largeWarehouse.restoreError'));
    } finally {
      setRestoring(false);
    }
  };

  // Zdejmuje oczekującą pozycję ze stacji — wpis magazynowy znika, więc wiersz na linii
  // przestaje być niebieski i pozycja wraca do zwykłego obiegu „Mojej produkcji".
  const handleRemoveConfirm = async () => {
    if (!removeItem) return;
    setRemoving(true);
    try {
      await api.delete(`/warehouse/large/${removeItem.id}`);
      await fetchItems();
      setRemoveItem(null);
    } catch (err: any) {
      alert(err?.response?.data?.message || t('largeWarehouse.removeError'));
    } finally {
      setRemoving(false);
    }
  };

  const fetchRewindHistory = async (warehouseItemId: number) => {
    setLoadingRewindHistory(true);
    try {
      const res = await api.get(`/warehouse/large/${warehouseItemId}/rewind-history`);
      setRewindHistory(res.data || []);
    } catch {
      setRewindHistory([]);
    } finally {
      setLoadingRewindHistory(false);
    }
  };

  const openHistoryModal = (item: WarehouseItem, shift: 1 | 2) => {
    setHistoryModal({ item, shift });
    fetchRewindHistory(item.id);
  };

  const closeHistoryModal = () => {
    setHistoryModal(null);
    setRewindHistory([]);
  };

  const handleSaveProg = async () => {
    setSavingProg(true);
    try {
      await api.put('/settings/waga_prog', { value: progInput });
      setWagaProg(progInput);
    } finally {
      setSavingProg(false);
    }
  };

  const lineOf = (item: WarehouseItem) => item.linia?.trim() || t('largeWarehouse.lineUnknown');
  const weekOf = (item: WarehouseItem) => item.tydzien?.trim() || t('largeWarehouse.weekUnknown');

  // Weeks present on the station, newest-looking first (numeric part descending when there is one).
  const availableWeeks = Array.from(new Set(items.map(weekOf))).sort((a, b) => {
    const na = parseInt(a.replace(/[^0-9]/g, ''), 10);
    const nb = parseInt(b.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(na) && !isNaN(nb) && na !== nb) return nb - na;
    return a.localeCompare(b);
  });

  // Weeks that disappeared from the station after a refresh no longer filter anything.
  const activeWeeks = weekFilter.filter((w) => availableWeeks.includes(w));
  const matchesWeek = (i: WarehouseItem) => activeWeeks.length === 0 || activeWeeks.includes(weekOf(i));
  const toggleWeek = (week: string) =>
    setWeekFilter((prev) => (prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week]));

  // Comma / semicolon separated phrases search for several items at once ("PLA-001, PLA-002");
  // within a phrase every word has to appear in one of the searched fields ("pla red" → PLA items in red).
  const searchPhrases = search
    .toLowerCase()
    .split(/[,;]/)
    .map((phrase) => phrase.trim().split(/\s+/).filter(Boolean))
    .filter((words) => words.length > 0);
  const matchesSearch = (i: WarehouseItem) => {
    if (searchPhrases.length === 0) return true;
    const haystack = [i.indeks_kat, i.nazwa_oryg, i.ean, i.kolor, i.material, i.marka]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return searchPhrases.some((words) => words.every((word) => haystack.includes(word)));
  };

  // Lines that disappeared from the station after a refresh no longer filter anything.
  const availableLines = new Set(items.map(lineOf));
  const activeLines = lineFilter.filter((l) => availableLines.has(l));
  const matchesLine = (i: WarehouseItem) => activeLines.length === 0 || activeLines.includes(lineOf(i));
  const toggleLine = (line: string) =>
    setLineFilter((prev) => (prev.includes(line) ? prev.filter((l) => l !== line) : [...prev, line]));

  const allPending = items.filter((i) => !i.przewinieta).filter(matchesWeek).filter(matchesSearch);
  const pending = allPending.filter(matchesLine);
  const done = items
    .filter((i) => i.przewinieta)
    .filter(matchesWeek)
    .filter(matchesSearch)
    .filter(matchesLine);

  // How many large spools await rewinding per production line (sumarycznie).
  // Always counted over every line so the summary stays complete while filtering by line
  // (the week filter does narrow it — the counts describe the selected week).
  // Sorted by count desc so the most loaded line reads first.
  const pendingByLine = Array.from(
    allPending.reduce((acc, item) => {
      acc.set(lineOf(item), (acc.get(lineOf(item)) ?? 0) + 1);
      return acc;
    }, new Map<string, number>())
  ).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  // Selected lines with nothing pending under the current week / search keep a chip (count 0)
  // so they can still be deselected.
  const lineChips: Array<[string, number]> = [
    ...pendingByLine,
    ...activeLines.filter((l) => !pendingByLine.some(([line]) => line === l)).map((l): [string, number] => [l, 0]),
  ];

  // The table is rendered with the ProductionTable component (1:1 like "My production");
  // priority is shown in its own ⭐ column and by a yellow row.
  // Z1/Z2 show per-shift rewound counters (Rewound = Z1 + Z2).
  const toTableItem = (item: WarehouseItem) => ({
    ...item,
    zmiana_1: item.rewound_z1 ? String(item.rewound_z1) : null,
    zmiana_2: item.rewound_z2 ? String(item.rewound_z2) : null,
    // Kolumna uwag pokazuje uwagę STACJI (large_warehouse_items.notes), nigdy uwagi linii.
    pivot: { notes: item.notes ?? null },
    rewinders_text: (item.rewinders ?? []).join(', ') || null,
    rewound_by_text: (item.rewound_by ?? []).join(', ') || null,
    transition_text: item.transition_kg ? t('table.transitionKg', { kg: formatKg(item.transition_kg) }) : null,
  });
  const noop = () => {};

  const hasActiveFilters = searchPhrases.length > 0 || activeWeeks.length > 0 || activeLines.length > 0;
  const clearFilters = () => {
    setSearch('');
    setWeekFilter([]);
    setLineFilter([]);
  };

  // Filter rows: a fixed label column with the chips wrapping next to it (below it on narrow screens).
  const filterRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' };
  const filterLabelStyle: React.CSSProperties = { width: 150, flexShrink: 0, paddingTop: 6 };
  const chipsStyle: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', flex: '1 1 300px', minWidth: 0 };
  const renderChip = (key: string, label: string, count: number | null, selected: boolean, onClick: () => void) => (
    <button
      key={key}
      onClick={onClick}
      aria-pressed={selected}
      style={{
        display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '6px 12px',
        border: `1px solid ${selected ? '#667eea' : '#e2e8f0'}`, borderRadius: 8,
        background: selected ? '#eef2ff' : '#fff', boxShadow: selected ? 'inset 0 0 0 1px #667eea' : 'none',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.3,
      }}
    >
      <span style={{ fontWeight: 700, color: selected ? '#4c51bf' : '#475569' }}>{label}</span>
      {count !== null && <span style={{ fontSize: 12, color: '#94a3b8' }}>{count}</span>}
    </button>
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>
            🏭 {t('largeWarehouse.title')}
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>
            {t('largeWarehouse.subtitle')}
          </p>
        </div>
        <AutoRefreshCountdown intervalSeconds={600} onRefresh={fetchItems} />
      </div>

      <div style={{
        background: '#eef2ff', border: '1px solid #667eea', borderLeft: '4px solid #667eea', borderRadius: 10,
        padding: '12px 16px', marginBottom: 24, color: '#334155', fontSize: 14,
      }}>
        👁️ {t('largeWarehouse.previewNotice')}
      </div>

      {/* Settings panel (manager only) */}
      {isManager && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{t('largeWarehouse.weightThresholdLabel')}</span>
          <input
            type="number"
            value={progInput}
            onChange={(e) => setProgInput(e.target.value)}
            style={{
              width: 100, padding: '6px 10px', border: '1px solid #cbd5e1',
              borderRadius: 6, fontSize: 14,
            }}
          />
          <span style={{ fontSize: 13, color: '#94a3b8' }}>{t('largeWarehouse.weightThresholdHelp')}</span>
          <button
            onClick={handleSaveProg}
            disabled={savingProg || progInput === wagaProg}
            style={{
              padding: '6px 16px', background: '#1e40af', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14,
              opacity: (savingProg || progInput === wagaProg) ? 0.5 : 1,
            }}
          >
            {savingProg ? t('largeWarehouse.saving') : t('largeWarehouse.save')}
          </button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{t('largeWarehouse.currentThreshold')} <strong>{wagaProg}</strong></span>
        </div>
      )}

      {/* Filters — search, weeks and lines narrow both tables and the stats below */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '16px 20px', marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
            {t('largeWarehouse.filtersTitle')}
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px', background: '#667eea', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ✕ {t('largeWarehouse.clearFilters')}
            </button>
          )}
        </div>

        <div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('largeWarehouse.searchPlaceholder')}
            aria-label={t('largeWarehouse.searchPlaceholder')}
            aria-describedby="large-warehouse-search-hint"
            style={{
              width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 14px',
              border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16, background: '#fff',
            }}
          />
          <div id="large-warehouse-search-hint" style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
            {t('largeWarehouse.searchHint')}
          </div>
        </div>

        {availableWeeks.length > 1 && (
          <div style={filterRowStyle}>
            <div style={filterLabelStyle}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{t('largeWarehouse.weekFilterLabel')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t('largeWarehouse.weekFilterHint')}</div>
            </div>
            <div style={chipsStyle}>
              {renderChip('all-weeks', t('largeWarehouse.weekFilterAll'), null, activeWeeks.length === 0, () => setWeekFilter([]))}
              {availableWeeks.map((week) => renderChip(
                week,
                week,
                items.filter((i) => weekOf(i) === week && !i.przewinieta).length,
                activeWeeks.includes(week),
                () => toggleWeek(week),
              ))}
            </div>
          </div>
        )}

        {!loading && lineChips.length > 0 && (
          <div style={filterRowStyle}>
            <div style={filterLabelStyle}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{t('largeWarehouse.lineFilterLabel')}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{t('largeWarehouse.lineFilterHint')}</div>
            </div>
            <div style={chipsStyle}>
              {renderChip('all-lines', t('largeWarehouse.lineFilterAll'), null, activeLines.length === 0, () => setLineFilter([]))}
              {lineChips.map(([line, count]) => renderChip(
                line,
                line,
                count,
                activeLines.includes(line),
                () => toggleLine(line),
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          // Oczekuje = niebieski (spójnie z wierszami: niebieski = do przewijania), nie pomarańczowy.
          { label: t('largeWarehouse.statsPending'), value: pending.length, color: '#2563eb', bg: '#eff6ff' },
          { label: t('largeWarehouse.statsDone'), value: done.length, color: '#10b981', bg: '#ecfdf5' },
          { label: t('largeWarehouse.statsTotal'), value: pending.length + done.length, color: '#6366f1', bg: '#eef2ff' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{
            flex: 1, minWidth: 140, background: bg, border: `1px solid ${color}30`,
            borderRadius: 10, padding: '14px 20px',
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>


      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{t('largeWarehouse.loading')}</div>
      ) : pending.length + done.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 48, background: '#f8fafc',
          borderRadius: 12, border: '1px dashed #e2e8f0', color: '#94a3b8',
        }}>
          {searchPhrases.length > 0
            ? t('largeWarehouse.searchEmpty', { q: search.trim() })
            : t('largeWarehouse.noItems')}
        </div>
      ) : (
        <>
          {/* Pending table */}
          {pending.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                ⏳ {t('largeWarehouse.pendingTitle', { count: pending.length })}
              </h3>
              <ProductionTable
                items={pending.map(toTableItem) as any}
                onRowClick={(item: any) => handleRowClick(item)}
                onNotesClick={(_e, item: any) => setNotesItem(item as WarehouseItem)}
                onQuantityClick={(_e, item: any, field) => openHistoryModal(item, field === 'zmiana_1' ? 1 : 2)}
                onBarwnikClick={noop}
                // Pending items are always blue; green only once the WorkSpectrum rewinding station
                // rewound all spools of the item. Priority = the ⭐ column.
                getRowColor={() => '#cfe2ff'}
                getItemStatus={() => 'pending'}
                labelOverrides={{ toProduce: t('largeWarehouse.colToRewind'), produced: t('largeWarehouse.colRewoundCount') }}
                showLineColumn
                showPriorityColumn
                showRewoundByColumns
                hideDyeColumns
                hideEanColumn
              />
            </div>
          )}

          {/* Done table */}
          {done.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                ✅ {t('largeWarehouse.doneTitle', { count: done.length })}
              </h3>
              {canRestoreRewinding && (
                <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#64748b' }}>
                  {t('largeWarehouse.restoreHint')}
                </p>
              )}
              <ProductionTable
                items={done.map(toTableItem) as any}
                onRowClick={(item: any) => { if (canRestoreRewinding) setRestoreItem(item as WarehouseItem); }}
                onNotesClick={(_e, item: any) => setNotesItem(item as WarehouseItem)}
                onQuantityClick={(_e, item: any, field) => openHistoryModal(item, field === 'zmiana_1' ? 1 : 2)}
                onBarwnikClick={noop}
                getRowColor={() => '#d4edda'}
                getItemStatus={() => 'completed'}
                labelOverrides={{ toProduce: t('largeWarehouse.colToRewind'), produced: t('largeWarehouse.colRewoundCount') }}
                showLineColumn
                showPriorityColumn
                showRewoundByColumns
                hideDyeColumns
                hideEanColumn
              />
            </div>
          )}
        </>
      )}

      {/* Rewound pieces per shift — read only, entries come from the WorkSpectrum rewinding station */}
      {historyModal && (() => {
        const max = historyModal.item.do_produkcji ? parseInt(historyModal.item.do_produkcji, 10) : null;
        const z1 = historyModal.item.rewound_z1 ?? 0;
        const z2 = historyModal.item.rewound_z2 ?? 0;
        const shiftName = historyModal.shift === 1 ? t('production.shift1') : t('production.shift2');
        const shiftHistory = rewindHistory.filter((e) => e.shift === historyModal.shift);
        return (
          <div
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: 'white', padding: '30px', borderRadius: '12px',
                maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <h3 style={{ marginTop: 0, color: '#667eea' }}>
                {t('largeWarehouse.historyModalTitle')} ({shiftName})
              </h3>
              <div style={{
                marginTop: '15px', padding: '12px', backgroundColor: '#e7f3ff',
                border: '1px solid #667eea', borderRadius: '8px', color: '#4a5568', fontSize: '12px',
              }}>
                📊 <strong>{historyModal.item.indeks_kat}</strong> — {historyModal.item.material}
                {max !== null && <> | {t('largeWarehouse.rewindModalMax', { max })}</>}
                {' '}| {t('largeWarehouse.rewindModalCurrent')} <strong>{z1 + z2}</strong> ({t('table.z1')}: {z1}, {t('table.z2')}: {z2})
              </div>
              <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#64748b' }}>
                {t('largeWarehouse.previewNotice')}
              </p>

              <div style={{ marginTop: '20px', borderTop: '2px solid #e1e8ed', paddingTop: '20px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#4a5568', fontSize: '16px', fontWeight: '600' }}>
                  📊 {t('myProduction.quantityChangeHistoryTitle', { shift: shiftName })}
                </h4>
                {loadingRewindHistory ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#667eea' }}>
                    {t('myProduction.loadingHistory')}
                  </div>
                ) : shiftHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '12px' }}>
                    {t('myProduction.noHistoryShift')}
                  </div>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e1e8ed' }}>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyDate')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyQuantityColumn')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('myProduction.historyOperator')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('table.rewinder')}
                          </th>
                          <th style={{ padding: '10px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>
                            {t('table.transition')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {shiftHistory.map((entry, index) => (
                          <tr
                            key={entry.id || index}
                            style={{
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                            }}
                          >
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.created_at
                                ? new Date(entry.created_at).toLocaleString(dateLocale, {
                                    year: 'numeric', month: '2-digit', day: '2-digit',
                                    hour: '2-digit', minute: '2-digit',
                                  })
                                : '-'}
                            </td>
                            <td style={{ padding: '10px', color: entry.quantity < 0 ? '#dc3545' : '#667eea', fontWeight: '600' }}>
                              {entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}
                            </td>
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.operator_name || entry.user?.name || t('myProduction.unknownUser')}
                            </td>
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.rewinder_name || '—'}
                            </td>
                            <td style={{ padding: '10px', color: '#4a5568' }}>
                              {entry.transition_kg ? t('table.transitionKg', { kg: formatKg(entry.transition_kg) }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeHistoryModal}
                  style={{
                    padding: '10px 20px', backgroundColor: '#6c757d', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cofnięcie oczekującej pozycji ze stacji do „Mojej produkcji" */}
      {removeItem && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>↩️</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.removeTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{removeItem.indeks_kat}</strong> &mdash; {removeItem.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.removeBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleRemoveConfirm}
                disabled={removing}
                style={{
                  flex: 1, padding: '13px 0', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: removing ? 'not-allowed' : 'pointer', opacity: removing ? 0.6 : 1,
                }}
              >
                {removing ? '...' : `↩️ ${t('largeWarehouse.removeConfirm')}`}
              </button>
              <button
                onClick={() => setRemoveItem(null)}
                disabled={removing}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uwagi stacji przewijania — osobne od uwag linii */}
      {notesItem && (
        <StationNotesModal
          warehouseItemId={notesItem.id}
          itemLabel={`${notesItem.indeks_kat || ''} — ${notesItem.material || ''}`}
          currentNote={notesItem.notes}
          noteBy={notesItem.notes_by}
          noteAt={notesItem.notes_at}
          problemTypes={problemTypes}
          dateLocale={dateLocale}
          onClose={() => setNotesItem(null)}
          onSaved={fetchItems}
        />
      )}

      {/* Przywrócenie pozycji z „Przewinięte" z powrotem do przewijania */}
      {restoreItem && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 32, maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>↩️</div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.restoreTitle')}
            </h2>
            <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 14 }}>
              <strong>{restoreItem.indeks_kat}</strong> &mdash; {restoreItem.material}
            </p>
            <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 13 }}>
              {t('largeWarehouse.restoreBody')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleRestoreConfirm}
                disabled={restoring}
                style={{
                  flex: 1, padding: '13px 0', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                  cursor: restoring ? 'not-allowed' : 'pointer', opacity: restoring ? 0.6 : 1,
                }}
              >
                {restoring ? '...' : `↩️ ${t('largeWarehouse.restoreConfirm')}`}
              </button>
              <button
                onClick={() => setRestoreItem(null)}
                disabled={restoring}
                style={{
                  flex: 1, padding: '13px 0', background: '#f1f5f9', color: '#64748b',
                  border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
